<template>
  <AppMainDrawer>
    <template #drawer>
      <DrawerStyled v-if="pageHasFilters[route.name]">
        <template #drawer-content>
          <ExpandableFilters />
        </template>
      </DrawerStyled>
    </template>

    <template #content>
      <div class="job-architecture-page">
        <TopBarForDrawer>
          <template #page-title>
            <TopBarForDrawerTitle
              :page-title="route.meta.title"
              :page-description="pageDescription"
              :is-loading="pageLoading"
            />
          </template>
          <template #summaries>
            <div
              v-if="!pageLoading"
              class="job-architecture-summaries"
            >
              <h3 class="text-body-2 text-textDarkest">
                Groups
                <span class="job-architecture-summary-value">
                  {{ numberWithCommas(architectureTotals.groups) }}
                </span>
              </h3>
              <h3 class="text-body-2 text-textDarkest">
                Families
                <span class="job-architecture-summary-value">
                  {{ numberWithCommas(architectureTotals.families) }}
                </span>
              </h3>
              <h3 class="text-body-2 text-textDarkest">
                Job Profiles
                <span class="job-architecture-summary-value">
                  {{ numberWithCommas(architectureTotals.profiles) }}
                </span>
              </h3>
            </div>
          </template>
          <template #topbar-actions>
            <div class="job-architecture-topbar-actions">
              <FiltersDrawerToggle
                v-if="pageHasFilters[route.name]"
                v-model="open"
                :is-loading="pageLoading"
                :are-filters-applied="areNonIgnorableFiltersApplied"
              />
              <JobArchitectureSearch />
              <v-btn
                variant="outlined"
                height="36"
                rounded="lg"
                class="download-csv-btn"
                :loading="xlsxDownloadLoading"
                :disabled="xlsxDownloadLoading || pageLoading || !hasListResults"
                @click="handleDownloadCsv(filteredTreeData)"
              >
                <v-icon
                  icon="$tray-arrow-down"
                  class="mr-2"
                  size="small"
                />
                Download CSV
              </v-btn>
              <v-btn
                color="brandPrimary"
                :disabled="!selectedProfileUlids.length"
                variant="flat"
                height="36"
                rounded="lg"
                @click="openCompare"
              >
                Compare Selected
                <template v-if="selectedProfileUlids.length">
                  ({{ selectedProfileUlids.length }})
                </template>
              </v-btn>
              <v-btn
                v-if="selectedProfileUlids.length"
                variant="text"
                color="brandPrimary"
                class="text-button-3"
                @click="clearSelectedProfileUlids"
              >
                Clear
              </v-btn>
            </div>
          </template>
        </TopBarForDrawer>

        <div class="job-architecture-list-wrapper">
          <JobArchitectureListView
            :key="listTreeExpandKey"
            v-model:selected-profiles="listSelectedProfileUlids"
            :tree-data="filteredTreeData"
          />
        </div>
      </div>
    </template>
  </AppMainDrawer>
</template>

<script setup>
import {
  computed,
  ref,
  nextTick,
  onMounted,
  onBeforeUnmount,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useJobArchitecture } from '@/stores/jobArchitecture/jobArchitecture';
import {
  useJobArchitectureFilters,
  mergeJobArchitectureRouteQuery,
} from '@/composables/useJobArchitectureFilters';
import { useFilters } from '@/stores/filters/filters';
import { useAppBase } from '@/stores/app/base';
import { useAppLoading } from '@/stores/app/loading';
import { useFilterDrawer } from '@/stores/filters/filterDrawer';
import { subscribeToFilterUpdates, unsubscribeToFilterUpdates } from '@/helpers/filterDispatcher';
import { pageDescriptions } from '@/helpers/constants/pageDescriptions';
import { tooltipKeys } from '@/helpers/constants/tooltipKeys';
import { useTooltips } from '@/stores/insights/tooltips';
import { useTracking } from '@/stores/tracking/tracking';
import { numberWithCommas } from '@/helpers/formatters';
import AppMainDrawer from '@/components/shared/AppMainDrawer.vue';
import DrawerStyled from '@/components/shared/DrawerStyled.vue';
import TopBarForDrawer from '@/components/shared/topbar/TopBarForDrawer.vue';
import TopBarForDrawerTitle from '@/components/shared/topbar/TopBarForDrawerTitle.vue';
import ExpandableFilters from '@/components/filter/ExpandableFilters.vue';
import FiltersDrawerToggle from '@/components/filter/FiltersDrawerToggle.vue';
import JobArchitectureListView from '@/components/jobArchitecture/JobArchitectureListView.vue';
import JobArchitectureSearch from '@/components/jobArchitecture/JobArchitectureSearch.vue';
import { collectExpandedNodeIdsForProfileUlids } from '@/components/jobArchitecture/helpers';

const route = useRoute();
const router = useRouter();

const listTreeExpandKey = ref(0);

const pageDescription = computed(() => pageDescriptions()[route.name] || '');

const { areNonIgnorableFiltersApplied, mergedTempFilters } = storeToRefs(useFilters());
const { pageHasFilters } = storeToRefs(useAppBase());
const { pageLoading } = storeToRefs(useAppLoading());
const { open } = storeToRefs(useFilterDrawer());

const { loadTooltips } = useTooltips();

const { setFilterConfig, clearPersistentFilters } = useFilters();
const { showFilterBarSettings } = useAppBase();

const jobArchitectureStore = useJobArchitecture();
const { xlsxDownloadLoading, selectedProfileUlids } = storeToRefs(jobArchitectureStore);
const {
  clearSelection,
  loadTree,
  downloadArchitectureCsv,
  setSelectedProfileUlids,
  clearSelectedProfileUlids,
  setListDefaultExpandedNodeIds,
  clearListDefaultExpandedNodeIds,
} = jobArchitectureStore;

const listSelectedProfileUlids = computed({
  get: () => selectedProfileUlids.value,
  set: (ulids) => setSelectedProfileUlids(ulids),
});

const {
  initializeFilters,
  filteredTreeData,
  clearSearch,
  reloadPage,
  saveListFilterSnapshotForCompare,
  searchText,
  selectedAlertCategoryIds,
  getAlertFilterExpansionIds,
} = useJobArchitectureFilters();

const architectureTotals = computed(() => {
  const groups = filteredTreeData.value?.groups || [];
  const families = groups.flatMap((group) => group.families || []);
  const profiles = families.flatMap((family) => family.profiles || []);

  return {
    groups: groups.length,
    families: families.length,
    profiles: profiles.length,
  };
});

const hasListResults = computed(() => architectureTotals.value.groups > 0);

const handleDownloadCsv = (treeData) => {
  useTracking().jaListDownloadedCsv();

  downloadArchitectureCsv(treeData);
};

const openCompare = () => {
  if (!selectedProfileUlids.value.length) return;

  useTracking().jaListClickedCompare();

  saveListFilterSnapshotForCompare();

  router.push({
    name: 'job-architecture-compare',
    query: { profiles: selectedProfileUlids.value.join(',') },
  });
};

const syncListTreeExpansion = async () => {
  await nextTick();

  const tree = filteredTreeData.value;
  let expanded = null;

  if (selectedAlertCategoryIds.value.length && tree?.groups?.length) {
    expanded = getAlertFilterExpansionIds();
  } else if (selectedProfileUlids.value.length && tree?.groups?.length) {
    expanded = collectExpandedNodeIdsForProfileUlids(tree, selectedProfileUlids.value);
  } else if (
    route.query.preview
    && route.query.type === 'jobProfileHris'
    && tree?.groups?.length
  ) {
    expanded = collectExpandedNodeIdsForProfileUlids(tree, [route.query.preview]);
  }

  setListDefaultExpandedNodeIds(expanded);
  listTreeExpandKey.value += 1;
};

const loadPage = async () => {
  reloadPage({
    router,
    routeQuery: route.query,
    mergedTempFilters: mergedTempFilters.value,
    applySidebarFilters: true,
  });

  await syncListTreeExpansion();
};

onMounted(async () => {
  subscribeToFilterUpdates(loadPage);

  setFilterConfig({
    module: 'jobArchitecture',
    showMyJobs: false,
    datepicker: false,
  });

  showFilterBarSettings({ route: route.name });

  loadTooltips(tooltipKeys['job-architecture']);

  clearSelection();

  await loadTree();

  const familyUlid = route.query.family;

  if (familyUlid) {
    const { family: _drop, ...queryRest } = route.query;

    router.replace({
      name: 'job-architecture-family',
      params: { familyUlid },
      query: queryRest,
    });

    return;
  }

  const restoredFromSnapshot = initializeFilters(route.query);

  if (restoredFromSnapshot) {
    router.replace({
      query: mergeJobArchitectureRouteQuery(
        route.query,
        mergedTempFilters.value,
        searchText.value,
      ),
    }).catch(() => true);
  }

  // Expand nodes that contain the selected profile ULIDs when returning from compare with a selection.
  await syncListTreeExpansion();
});

onBeforeUnmount(() => {
  unsubscribeToFilterUpdates();

  clearPersistentFilters();

  clearSearch();

  clearListDefaultExpandedNodeIds();

  setFilterConfig({
    subMenuType: null,
  });
});
</script>

<style scoped lang="less">
@import '@/less/mixins';

.job-architecture-summaries {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: flex-end;
  margin-left: 12px;
}

.job-architecture-summary-value {
  color: @text-brand;
}

.job-architecture-page {
  display: flex;
  flex-direction: column;
  width: 100%;

  @media screen and (min-width: 1800px) {
    max-width: 1400px;
  }
}

.job-architecture-list-wrapper {
  flex-shrink: 0;
  margin: 0 24px 16px;
  border: 1px solid @stroke-default;
  border-top: none;
  border-radius: 8px;
  background: @bg-default;
}

.job-architecture-page :deep(.topbar-actions) {
  width: 100%;
  min-height: 40px;
}

.job-architecture-topbar-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;
  min-height: 40px;
  gap: 8px;
  row-gap: 10px;
}

.download-csv-btn {
  margin-left: auto;
  height: 40px !important;
  min-height: 40px !important;
  padding: 0 12px;
  border-radius: 8px !important;
  border: 1px solid @stroke-default;
  color: @text-dark;
  background: @white;
}
</style>
